import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchMemorySnapshot } from '../../utils/api/streamBrain';
import { parseMemoryHeader } from './parseMemoryHeader';
import { buildMemoryTree } from './buildMemoryTree';
import './MemoryView.css';

function fileStatus(files, path) {
  const parsed = parseMemoryHeader(files[path] || '');
  return parsed.header?.status || '';
}

function TreeRows({ nodes, depth, selectedPath, onSelect, openFolders, onToggleFolder, files }) {
  return nodes.map((node) => {
    if (node.type === 'section') {
      return (
        <div key={node.label} className="memory-view__section">
          <div className="memory-view__section-label">{node.label}</div>
          <TreeRows
            nodes={node.children}
            depth={0}
            selectedPath={selectedPath}
            onSelect={onSelect}
            openFolders={openFolders}
            onToggleFolder={onToggleFolder}
            files={files}
          />
        </div>
      );
    }

    if (node.type === 'folder') {
      const open = openFolders.has(node.path);
      return (
        <div key={node.path}>
          <button
            type="button"
            className="memory-view__row memory-view__row--folder"
            style={{ paddingLeft: 10 + depth * 14 }}
            onClick={() => onToggleFolder(node.path)}
          >
            <span className={`memory-view__chevron ${open ? 'is-open' : ''}`} aria-hidden>
              ▸
            </span>
            <span className="memory-view__name">{node.label}</span>
            {node.isPackage && <span className="memory-view__package">package</span>}
          </button>
          {open && (
            <TreeRows
              nodes={node.children}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              openFolders={openFolders}
              onToggleFolder={onToggleFolder}
              files={files}
            />
          )}
        </div>
      );
    }

    const status = fileStatus(files, node.path);
    return (
      <button
        key={node.path}
        type="button"
        className={`memory-view__row ${selectedPath === node.path ? 'is-selected' : ''}`}
        style={{ paddingLeft: 28 + depth * 14 }}
        onClick={() => onSelect(node.path)}
      >
        <span className="memory-view__name">{node.label}</span>
        {status && (
          <span className={`memory-view__status memory-view__status--${status}`}>{status}</span>
        )}
      </button>
    );
  });
}

const MemoryView = ({ isOpen = false, onClose, workspaceId }) => {
  const [files, setFiles] = useState({});
  const [selectedPath, setSelectedPath] = useState('');
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [hotSet, setHotSet] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setStatus('loading');
    setError('');
    fetchMemorySnapshot(workspaceId)
      .then((data) => {
        if (cancelled) return;
        const next = {};
        for (const file of data.files || []) next[file.path] = file.body;
        setFiles(next);
        setSelectedPath((prev) => (prev && next[prev] ? prev : Object.keys(next)[0] || ''));
        setOpenFolders(new Set(Object.keys(next).filter((path) => path.includes('/')).map((path) => path.split('/').slice(0, -1).join('/'))));
        setHotSet(data.hotSet || null);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Could not load memory.');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceId]);

  const tree = useMemo(() => buildMemoryTree(Object.keys(files).map((path) => ({ path }))), [files]);
  const raw = files[selectedPath] || '';
  const parsed = useMemo(() => parseMemoryHeader(raw), [raw]);

  const toggleFolder = (path) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  if (!isOpen) return null;

  const header = parsed.header;

  return createPortal(
    <div className="memory-view__overlay" onClick={onClose}>
      <div
        className="memory-view"
        role="dialog"
        aria-label="Memory"
        onClick={(e) => e.stopPropagation()}
      >
        <aside className="memory-view__tree">
          <div className="memory-view__tree-head">
            <div className="memory-view__eyebrow">Founder</div>
            <h2 className="memory-view__tree-title">Memory</h2>
            <p className="memory-view__tree-note">
              {status === 'loading'
                ? 'Loading…'
                : hotSet
                  ? `Hot set ${hotSet.tokens} / ${hotSet.budget}`
                  : 'Live files · read only'}
            </p>
          </div>
          <div className="memory-view__tree-list">
            {status === 'error' && <p className="memory-view__tree-note">{error}</p>}
            {status === 'ready' && !tree.length && (
              <p className="memory-view__tree-note">Send a message to seed this brain.</p>
            )}
            <TreeRows
              nodes={tree}
              depth={0}
              selectedPath={selectedPath}
              onSelect={setSelectedPath}
              openFolders={openFolders}
              onToggleFolder={toggleFolder}
              files={files}
            />
          </div>
        </aside>

        <div className="memory-view__main">
          <header className="memory-view__main-head">
            <div>
              <div className="memory-view__eyebrow">File</div>
              <h3 className="memory-view__path">{selectedPath || '—'}</h3>
            </div>
            <button
              type="button"
              className="memory-view__close"
              onClick={onClose}
              aria-label="Close Memory"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          {header && (
            <div className="memory-view__card">
              <div className="memory-view__card-row">
                <span className="memory-view__card-key">Summary</span>
                <span className="memory-view__card-val">{header.summary || '—'}</span>
              </div>
              <div className="memory-view__card-row">
                <span className="memory-view__card-key">Cues</span>
                <span className="memory-view__card-val">{header.cues || '—'}</span>
              </div>
              {header.abstract && (
                <div className="memory-view__card-row">
                  <span className="memory-view__card-key">Abstract</span>
                  <span className="memory-view__card-val">{header.abstract}</span>
                </div>
              )}
              <div className="memory-view__card-meta">
                <span>Updated {header.updated || '—'}</span>
                {header.status && (
                  <span className={`memory-view__status memory-view__status--${header.status}`}>
                    {header.status}
                  </span>
                )}
              </div>
            </div>
          )}

          <pre className="memory-view__body">{parsed.body || 'This file is empty.'}</pre>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MemoryView;
